import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useWorker } from "../context/WorkerContext";

// ─────────────────────────────────────────────────────────────────────────────
// SVG Icons
// ─────────────────────────────────────────────────────────────────────────────

const PaymentHistoryIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="6" width="26" height="30" rx="3" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="32" cy="32" r="8" fill="#f0f0f5" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="32" y1="28" x2="32" y2="32" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
    <line x1="32" y1="32" x2="35" y2="35" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
    <line x1="11" y1="14" x2="21" y2="14" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
    <line x1="11" y1="19" x2="21" y2="19" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
    <line x1="11" y1="24" x2="18" y2="24" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IncentivePayoutIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="12" width="32" height="22" rx="3" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="34" cy="28" r="5" fill="#f0f0f5" stroke="#6B4EFF" strokeWidth="2"/>
    <circle cx="34" cy="28" r="2" fill="#6B4EFF"/>
    <line x1="6" y1="18" x2="38" y2="18" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="10" y1="24" x2="16" y2="24" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const ReferralPayoutIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="22" cy="30" r="4" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="10" cy="30" r="4" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="34" cy="30" r="4" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="22" cy="14" r="4" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <line x1="22" y1="18" x2="22" y2="26" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="22" y1="26" x2="10" y2="26" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="22" y1="26" x2="34" y2="26" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="10" y1="26" x2="10" y2="26" stroke="#6B4EFF" strokeWidth="2"/>
  </svg>
);


// Main Component: PaymentHistory
 

const PaymentHistory = () => {
  const navigate = useNavigate();
  const { token, setToken, logout } = useWorker();
  const [accountDetails, setAccountDetails] = useState({ accountNumber: "" });

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // ── Helpers ────────────────────────────────────────────────────────

  const formatAccountNumber = (accountNo) => {
    if (!accountNo) return "";
    const first = accountNo.charAt(0);
    const last4 = accountNo.slice(-4);
    return `${first}${"X".repeat(accountNo.length - 5)}${last4}`;
  };

  // ── Auth guard ─────────────────────────────────────────────────────

  const verifyTokenWithAuth = useCallback(
    async (authToken) => {
      try {
        const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.data?.error?.name === "TokenExpiredError") {
          localStorage.removeItem("auth_token");
          return false;
        }
        return true;
      } catch (error) {
        console.error("Token verification failed:", error);
        localStorage.removeItem("auth_token");
        if (setToken && typeof setToken === "function") setToken(null);
        if (logout && typeof logout === "function") logout();
        return false;
      }
    },
    [setToken, logout, BASE_URL]
  );

  useEffect(() => {
    const checkTokenValidity = async () => {
      if (!token) { navigate("/lkart"); return; }
      const isTokenValid = await verifyTokenWithAuth(token);
      if (!isTokenValid) navigate("/lkart");
    };
    checkTokenValidity();
  }, [token, navigate, verifyTokenWithAuth]);

  // ── Data fetching ──────────────────────────────────────────────────

  const fetchAccountDetails = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/account-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.accountNumber) setAccountDetails(response.data);
    } catch (error) {
      console.error("Error fetching account details:", error);
    }
  }, [token, BASE_URL]);

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  // ── Handlers ───────────────────────────────────────────────────────

  const handlePaymentHistoryClick = () => {
    navigate("/lkart/dashboard/payment-history");
  };

  const handlePaymentSettingClick = () =>{
    navigate("/lkart/dashboard/payment-setting")
  }

  const handleReferralPayoutsClick = () => {
    navigate("/lkart/dashboard/referral-payout");
  };

  const handleIncentivePayoutClick = () => {
    navigate("/lkart/dashboard/incentive-payout");
  };

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f0f0f5",
        display: "flex",
        flexDirection: "column",
        paddingTop: "74px",
      }}
    >
      {/* Header */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 20px",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            position: "absolute",
            left: "20px",
            background: "none",
            border: "none",
            fontSize: "28px",
            color: "#222",
            cursor: "pointer",
            lineHeight: 1,
            padding: "0 4px",
          }}
        >
          ‹
        </button>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "700",
            color: "#111",
            margin: 0,
          }}
        >
          My Payouts
        </h2>
      </div>

      {/* Cards — centered vertically in remaining space */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            width: "100%",
            maxWidth: "420px",
          }}
        >
          {/* Payment History */}
          <button
            onClick={handlePaymentHistoryClick}
            style={cardStyle}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          >
            <PaymentHistoryIcon />
            <span style={labelStyle}>Payment History</span>
          </button>

          {/* Incentive Payout */}
          <button
            onClick={handleIncentivePayoutClick}
            style={cardStyle}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          >
            <IncentivePayoutIcon />
            <span style={labelStyle}>Incentive Payout</span>
          </button>

          {/* Referral Payout — left-aligned in grid (col 1) */}
          <button
            onClick={handleReferralPayoutsClick}
            style={cardStyle}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          >
            <ReferralPayoutIcon />
            <span style={labelStyle}>Referral payout</span>
          </button>

           {/* payout setting */}
           <button
            onClick={handlePaymentSettingClick}
            style={cardStyle}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          >
            <PaymentHistoryIcon />
            <span style={labelStyle}>Payout Settings</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Shared styles ────────────────────────────────────────────────────────────

const cardStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "20px 18px 18px",
  backgroundColor: "#eeeef8",
  border: "none",
  borderRadius: "18px",
  cursor: "pointer",
  transition: "transform 0.15s ease, box-shadow 0.15s ease",
  minHeight: "120px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  textAlign: "left",
};

const labelStyle = {
  fontSize: "14px",
  fontWeight: "500",
  color: "#111",
  lineHeight: "1.3",
};

export default PaymentHistory;